import React from 'react';
import { MessageSquare, FileEdit, RotateCcw, Calendar, Zap } from 'lucide-react';
import { NiwtAgent } from '../types';
import { emitNiwtAsk, emitNiwtDraft, emitNiwtFollowUp, emitNiwtPrepMeeting } from '../events';

const QUICK_ACTIONS = [
  { label: 'Ask', icon: MessageSquare, emit: emitNiwtAsk },
  { label: 'Draft', icon: FileEdit, emit: emitNiwtDraft },
  { label: 'Follow-up', icon: RotateCcw, emit: emitNiwtFollowUp },
  { label: 'Prep meeting', icon: Calendar, emit: emitNiwtPrepMeeting },
];

function freshnessLabel(minutes: number): string {
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

interface AgentAvatarProps {
  agent: NiwtAgent;
}

const AgentAvatar: React.FC<AgentAvatarProps> = ({ agent }) => {
  const initials = agent.display_name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const isNiwtco = agent.type === 'niwtco';

  return (
    <div
      className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
        isNiwtco
          ? 'bg-gradient-to-br from-niwt-primary to-niwt-secondary text-white'
          : 'bg-gray-100 text-gray-600'
      }`}
    >
      {isNiwtco ? <Zap className="w-4 h-4" /> : initials}
    </div>
  );
};

interface Props {
  agents: NiwtAgent[];
}

const EngageANiwtPanel: React.FC<Props> = ({ agents }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
      <div className="flex items-center gap-1.5 mb-3">
        <Zap className="w-4 h-4 text-niwt-primary" />
        <h2 className="text-sm font-semibold text-gray-800">Engage a Niwt</h2>
      </div>

      <div className="space-y-3">
        {agents.map((agent) => (
          <div key={agent.id} className="border border-gray-100 rounded-lg p-3">
            {/* Agent header */}
            <div className="flex items-center gap-2.5 mb-2.5">
              <AgentAvatar agent={agent} />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-900 truncate">{agent.display_name}</p>
                <p className="text-xs text-gray-400 truncate">
                  {agent.role} · {agent.coverage}% coverage · {freshnessLabel(agent.freshness_minutes)}
                </p>
              </div>
            </div>

            {/* Quick actions */}
            <div className="grid grid-cols-2 gap-1.5">
              {QUICK_ACTIONS.map(({ label, icon: Icon, emit }) => (
                <button
                  key={label}
                  onClick={() => emit(agent.id)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 hover:bg-purple-50 hover:text-niwt-primary rounded-lg border border-gray-200 hover:border-niwt-primary/30 transition-colors"
                >
                  <Icon className="w-3 h-3" />
                  {label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EngageANiwtPanel;
